"use strict";
class PostcodeStepController {
  constructor(containerId) {
    const container = document.getElementById(containerId);
    if (!container) {
      throw new Error(`Wizard container #${containerId} not found`);
    }
    this.container = container;
    this.loadingState = { isBusy: false };
    this.input = this.queryElement("#wizard-postcode");
    this.nextBtn = this.queryElement("#postcode-next-btn");
    this.errorText = this.queryElement("#postcode-error");
    this.initEventListeners();
  }
  /**
   * Helper for DOM querying within the component
   */
  queryElement(selector) {
    const el = this.container.querySelector(selector);
    if (!el) {
      throw new Error(`Element ${selector} not found within wizard container`);
    }
    return el;
  }
  initEventListeners() {
    this.input.addEventListener("input", () => {
      this.handleInputFormatting();
    });
    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.validateAndProceed();
      }
    });
    this.nextBtn.addEventListener("click", () => {
      this.validateAndProceed();
    });
  }
  /**
   * Formats postcode input:
   * - Forces Uppercase
   * - Removes non-alphanumeric chars
   * - Inserts space after 4 digits
   */
  handleInputFormatting() {
    let value = this.input.value.toUpperCase().replaceAll(/[^A-Z0-9]/g, "");
    if (value.length > 4) {
      value = `${value.slice(0, 4)} ${value.slice(4, 6)}`;
    }
    this.input.value = value.slice(0, 7);
    this.hideError();
  }
  /**
   * Main validation flow
   */
  async validateAndProceed() {
    if (this.loadingState.isBusy) return;
    const postcode = this.input.value.trim();
    const dutchPostcodeRegex = /^[1-9]\d{3}\s?[A-Z]{2}$/;
    if (!dutchPostcodeRegex.test(postcode)) {
      this.showError("Vul een geldige postcode in (bijv. 1701 AB)");
      this.input.focus();
      return;
    }
    this.setLoading(true);
    try {
      const response = await this.checkServiceArea(postcode);
      if (response.success) {
        const event = new CustomEvent("wizard:step-complete", {
          detail: {
            step: 0,
            data: { postcode: response.normalizedPostcode }
          },
          bubbles: true
        });
        this.container.dispatchEvent(event);
      } else {
        this.showError(response.message);
      }
    } catch (error) {
      console.error("Postcode validation error:", error);
      this.showError("Er is een technische fout opgetreden. Probeer het later opnieuw.");
    } finally {
      this.setLoading(false);
    }
  }
  /**
   * Network Layer: Calls backend API
   */
  async checkServiceArea(postcode) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content ?? "";
    const res = await fetch("/api/wizard/validate-postcode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ postcode })
    });
    if (!res.ok) {
      throw new Error(`Server responded with ${res.status}`);
    }
    return res.json();
  }
  /**
   * UI State: Error Handling
   * Manages ARIA attributes for accessibility
   */
  showError(message) {
    this.errorText.textContent = message;
    this.errorText.classList.remove("hidden");
    this.input.setAttribute("aria-invalid", "true");
    this.errorText.setAttribute("role", "alert");
    this.input.classList.add("border-[#D40032]", "bg-red-50");
    this.input.classList.remove("border-neutral-200");
    this.input.parentElement?.classList.add("animate-shake");
    setTimeout(() => this.input.parentElement?.classList.remove("animate-shake"), 300);
  }
  hideError() {
    this.errorText.classList.add("hidden");
    this.input.setAttribute("aria-invalid", "false");
    this.errorText.removeAttribute("role");
    this.input.classList.remove("border-[#D40032]", "bg-red-50");
    this.input.classList.add("border-neutral-200");
  }
  /**
   * UI State: Loading
   */
  setLoading(isLoading) {
    this.loadingState.isBusy = isLoading;
    this.nextBtn.disabled = isLoading;
    this.input.disabled = isLoading;
    if (isLoading) {
      const originalText = this.nextBtn.textContent;
      this.nextBtn.dataset.originalText = originalText || "Volgende";
      this.nextBtn.innerHTML = `
        <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
      `;
    } else {
      this.nextBtn.textContent = this.nextBtn.dataset.originalText || "Volgende";
      this.nextBtn.innerHTML = 'Volgende <i data-lucide="arrow-right" class="w-5 h-5 inline-block ml-2"></i>';
      if (globalThis.lucide) {
        globalThis.lucide.createIcons({ nodes: [this.nextBtn] });
      }
    }
  }
}
globalThis.PostcodeStepController = PostcodeStepController;
